<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import MeetingsAPI from 'dashboard/api/meetings';
import AgendaMeetingModal from 'dashboard/modules/agenda/components/AgendaMeetingModal.vue';

const props = defineProps({
  card: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const meetings = ref([]);
const isLoading = ref(false);
const openMeetingId = ref(null);
const showCreateForm = ref(false);

const contactId = computed(() => props.card?.contact?.id ?? null);

const STATUS_OPTIONS = [
  { key: 'scheduled', label: 'Agendada', color: 'text-n-blue-11 bg-n-blue-2' },
  { key: 'done', label: 'Realizada', color: 'text-n-teal-11 bg-n-teal-2' },
  { key: 'no_show', label: 'No-show', color: 'text-n-ruby-11 bg-n-ruby-2' },
  {
    key: 'rescheduled',
    label: 'Reagendada',
    color: 'text-n-amber-11 bg-n-amber-2',
  },
  {
    key: 'cancelled',
    label: 'Cancelada',
    color: 'text-n-slate-10 bg-n-alpha-2',
  },
];

function statusOption(key) {
  return STATUS_OPTIONS.find(o => o.key === key) ?? STATUS_OPTIONS[0];
}

async function fetchMeetings() {
  if (!contactId.value) return;
  isLoading.value = true;
  try {
    const { data } = await MeetingsAPI.list({ contact_id: contactId.value });
    meetings.value = Array.isArray(data) ? data : (data?.data ?? []);
  } catch {
    meetings.value = [];
  } finally {
    isLoading.value = false;
  }
}

onMounted(fetchMeetings);

function openMeeting(id) {
  openMeetingId.value = id;
}

function closeMeeting() {
  openMeetingId.value = null;
}

async function onMeetingUpdated() {
  await fetchMeetings();
}

async function onMeetingDeleted() {
  openMeetingId.value = null;
  await fetchMeetings();
}

// ── formulário de criação ─────────────────────────────────────────
const newTitle = ref('');
const newStartAt = ref('');
const newEndAt = ref('');
const isSaving = ref(false);

function openForm() {
  newTitle.value = '';
  newStartAt.value = '';
  newEndAt.value = '';
  showCreateForm.value = true;
}

function cancelForm() {
  showCreateForm.value = false;
}

async function createMeeting() {
  if (!newTitle.value || !newStartAt.value || !newEndAt.value) return;
  isSaving.value = true;
  try {
    const created = await store.dispatch('agenda/createMeeting', {
      title: newTitle.value,
      start_at: new Date(newStartAt.value).toISOString(),
      end_at: new Date(newEndAt.value).toISOString(),
      contact_id: contactId.value || null,
    });
    if (created) {
      meetings.value = [created, ...meetings.value];
      showCreateForm.value = false;
    }
  } finally {
    isSaving.value = false;
  }
}

function formatTime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatMonth(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d
    .toLocaleString('pt-BR', { month: 'short' })
    .replace('.', '')
    .toUpperCase();
}

function formatDay(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).getDate();
}
</script>

<template>
  <div class="flex flex-col h-full">
    <div class="flex items-center justify-between">
      <h4 class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider">
        {{ t('KANBAN.OPPORTUNITY_MODAL.NAV_CALENDAR') }}
      </h4>
      <button
        class="flex items-center gap-1 text-xs text-n-brand hover:brightness-110"
        @click="openForm"
      >
        <span class="i-lucide-plus size-3.5" />
        {{ t('KANBAN.CALENDAR.SCHEDULE_BTN') }}
      </button>
    </div>

    <!-- Formulário de criação -->
    <div
      v-if="showCreateForm"
      class="mt-3 mb-4 flex flex-col gap-2 p-3 bg-n-surface-2 rounded-lg border border-n-weak"
    >
      <input
        v-model="newTitle"
        type="text"
        :placeholder="t('KANBAN.CALENDAR.MEETING_TITLE_PLACEHOLDER')"
        class="w-full text-sm font-medium text-n-slate-12 bg-transparent border-b border-n-weak pb-1 focus:outline-none"
        autofocus
      />
      <div class="flex flex-col gap-1">
        <label class="text-[10px] text-n-slate-9 uppercase font-semibold">{{
          t('KANBAN.CALENDAR.START_LABEL')
        }}</label>
        <input
          v-model="newStartAt"
          type="datetime-local"
          class="w-full text-sm px-3 py-2 rounded border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-[10px] text-n-slate-9 uppercase font-semibold">{{
          t('KANBAN.CALENDAR.END_LABEL')
        }}</label>
        <input
          v-model="newEndAt"
          type="datetime-local"
          class="w-full text-sm px-3 py-2 rounded border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
        />
      </div>
      <div class="flex gap-2 justify-end">
        <button
          class="text-xs text-n-slate-10 hover:text-n-slate-12"
          @click="cancelForm"
        >
          {{ t('KANBAN.CALENDAR.CANCEL') }}
        </button>
        <button
          :disabled="isSaving || !newTitle || !newStartAt || !newEndAt"
          class="text-xs px-3 py-1.5 bg-n-brand text-white rounded disabled:opacity-40"
          @click="createMeeting"
        >
          {{
            isSaving ? t('KANBAN.CALENDAR.SAVING') : t('KANBAN.CALENDAR.SAVE')
          }}
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-8">
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-9" />
    </div>

    <!-- Sem contato -->
    <div
      v-else-if="!contactId"
      class="flex flex-col items-center py-12 gap-2 text-n-slate-9"
    >
      <span class="i-lucide-user-x size-8" />
      <span class="text-sm">{{ t('KANBAN.CALENDAR.NO_CONTACT') }}</span>
    </div>

    <!-- Vazio -->
    <div
      v-else-if="meetings.length === 0"
      class="flex flex-col items-center py-12 gap-2 text-n-slate-9"
    >
      <span class="i-lucide-calendar-x size-8" />
      <span class="text-sm">{{ t('KANBAN.CALENDAR.EMPTY') }}</span>
    </div>

    <!-- Lista de reuniões -->
    <div v-else class="mt-3 space-y-2">
      <button
        v-for="meeting in meetings"
        :key="meeting.id"
        class="w-full flex items-center gap-2 p-2 rounded-lg bg-n-surface-2 border border-n-weak hover:border-n-container transition-colors text-left"
        @click="openMeeting(meeting.id)"
      >
        <!-- Data compacta -->
        <div
          class="flex flex-col items-center justify-center min-w-[36px] text-center flex-shrink-0"
        >
          <span
            class="text-[10px] font-semibold text-n-brand uppercase leading-none"
          >
            {{ formatMonth(meeting.start_at) }}
          </span>
          <span class="text-base font-bold text-n-slate-12 leading-tight">
            {{ formatDay(meeting.start_at) }}
          </span>
        </div>

        <!-- Título + hora + status -->
        <div class="flex-1 min-w-0">
          <p class="text-xs font-medium text-n-slate-12 truncate">
            {{ meeting.title }}
          </p>
          <div class="flex items-center gap-2 mt-0.5">
            <span class="text-[11px] text-n-slate-9">
              {{ formatTime(meeting.start_at) }}
              <template v-if="meeting.end_at">
                {{ ' – ' }}{{ formatTime(meeting.end_at) }}
              </template>
            </span>
            <span
              class="text-[11px] font-medium px-1.5 py-0.5 rounded"
              :class="statusOption(meeting.status).color"
            >
              {{ statusOption(meeting.status).label }}
            </span>
          </div>
        </div>

        <span
          class="i-lucide-chevron-right size-3.5 text-n-slate-9 flex-shrink-0"
        />
      </button>
    </div>

    <!-- Modal de detalhes da reunião -->
    <AgendaMeetingModal
      v-if="openMeetingId"
      :meeting-id="openMeetingId"
      @close="closeMeeting"
      @updated="onMeetingUpdated"
      @deleted="onMeetingDeleted"
    />
  </div>
</template>
